import { createSlice } from "@reduxjs/toolkit";
import axios from "axios";

export const userManagementSlide = createSlice({
    name: "userManagement",
    initialState: {
        page: 0,
        pageCount: 0,
        totalRow: 0,
        success: false,
        fail: false,
        listData: null,
        detailData: null,
        responseData: null,
        errorDetailData: null,
        summaryData: null,
        assignableRoles: null,
    },
    reducers: {
        doGetUserManagementListSuccess: (state, action) => {
            state.listData = action.payload.data;
            // customResponse.success spreads getPagingData at the TOP level, not
            // under `data`. The previous version read action.payload.data?.current_page,
            // so page and pageCount were silently undefined and the pager always
            // showed "page 1 of 1".
            state.page = action.payload?.current_page;
            state.pageCount = action.payload?.page_count;
            state.totalRow = action.payload?.total_row;
        },
        doGetUserManagementListFail: (state, action) => {
            state.listData = null;
            state.page = 0;
            state.pageCount = 0;
            state.totalRow = 0;
        },
        doGetUserManagementSummarySuccess: (state, action) => {
            state.summaryData = action.payload?.data;
        },
        doGetUserManagementSummaryFail: (state, action) => {
            state.summaryData = null;
        },
        doGetAssignableRolesSuccess: (state, action) => {
            state.assignableRoles = action.payload?.data;
        },
        doGetAssignableRolesFail: (state, action) => {
            state.assignableRoles = null;
        },
        doGetUserManagementDetailSuccess: (state, action) => {
            state.detailData = action.payload.data;
        },
        doGetUserManagementDetailFail: (state, action) => {
            state.detailData = null;
        },
        doAddUserManagementSuccess: (state, action) => {
            state.success = true;
            state.fail = false;
            state.responseData = action.payload;
        },
        doAddUserManagementFail: (state, action) => {
            state.success = false;
            state.fail = true;
            state.errorDetailData = action.payload;
        },
        doUpdateUserManagementSuccess: (state, action) => {
            state.success = true;
            state.fail = false;
        },
        doUpdateUserManagementFail: (state, action) => {
            state.success = false;
            state.fail = true;
            state.errorDetailData = action.payload;
        },
        resetUserManagementStateSuccess: (state, action) => {
            state.success = action.payload;
            state.fail = action.payload;
            state.detailData = null;
            state.errorDetailData = null;
        },
        // Clears the error WITHOUT nulling detailData. Dismissing a validation
        // error on the Edit screen used to blank the form's source data.
        resetUserManagementErrorState: (state, action) => {
            state.success = false;
            state.fail = false;
            state.errorDetailData = null;
        },
    },
});

export const doGetUserManagementList = (param) => async (dispatch) => {
    try {
        let url = `/api/admin/user-management?page=${param.page}&size=${param.limit}`;
        if (param.filter) {
            url += `&filter=${encodeURIComponent(param.filter)}`;
        }
        if (param.role_id) {
            url += `&role_id=${param.role_id}`;
        }
        if (param.status) {
            url += `&status=${param.status}`;
        }

        const response = await axios.get(url);
        dispatch(doGetUserManagementListSuccess(response.data));
    } catch (error) {
        dispatch(doGetUserManagementListFail(error.response));
    }
};

export const doGetUserManagementSummary = () => async (dispatch) => {
    try {
        const response = await axios.get(`/api/admin/user-management/summary`);
        dispatch(doGetUserManagementSummarySuccess(response.data));
    } catch (error) {
        dispatch(doGetUserManagementSummaryFail(error.response));
    }
};

// Roles this caller may actually hand out, so the dropdown can never offer
// something the save would reject.
export const doGetAssignableRoles = () => async (dispatch) => {
    try {
        const response = await axios.get(`/api/admin/user-management/roles`);
        dispatch(doGetAssignableRolesSuccess(response.data));
    } catch (error) {
        dispatch(doGetAssignableRolesFail(error.response));
    }
};

export const doGetUserManagementDetail = (param) => async (dispatch) => {
    try {
        const response = await axios.get(`/api/admin/user-management/${param.user_id}`);
        dispatch(doGetUserManagementDetailSuccess(response.data));
    } catch (error) {
        dispatch(doGetUserManagementDetailFail(error.response));
    }
};

export const doAddUserManagement = (param) => async (dispatch) => {
    try {
        const response = await axios.post(`/api/admin/user-management`, param);
        dispatch(doAddUserManagementSuccess(response.data));
    } catch (error) {
        dispatch(doAddUserManagementFail(error.response));
    }
};

export const doUpdateUserManagement = (param, user_id) => async (dispatch) => {
    try {
        const response = await axios.put(`/api/admin/user-management/${user_id}`, param);
        dispatch(doUpdateUserManagementSuccess(response.data));
    } catch (error) {
        dispatch(doUpdateUserManagementFail(error.response));
    }
};

export const doUpdateUserManagementStatus = (param, user_id) => async (dispatch) => {
    try {
        const response = await axios.put(`/api/admin/user-management/${user_id}/status`, param);
        dispatch(doUpdateUserManagementSuccess(response.data));
    } catch (error) {
        dispatch(doUpdateUserManagementFail(error.response));
    }
};

export const doResetUserManagementPassword = (param, user_id) => async (dispatch) => {
    try {
        const response = await axios.put(`/api/admin/user-management/${user_id}/password`, param);
        dispatch(doUpdateUserManagementSuccess(response.data));
    } catch (error) {
        dispatch(doUpdateUserManagementFail(error.response));
    }
};

export const resetUserManagementState = () => async (dispatch) => {
    dispatch(resetUserManagementStateSuccess(false));
};

export const {
    doGetUserManagementListSuccess,
    doGetUserManagementListFail,
    doGetUserManagementSummarySuccess,
    doGetUserManagementSummaryFail,
    doGetAssignableRolesSuccess,
    doGetAssignableRolesFail,
    doGetUserManagementDetailSuccess,
    doGetUserManagementDetailFail,
    doAddUserManagementSuccess,
    doAddUserManagementFail,
    doUpdateUserManagementSuccess,
    doUpdateUserManagementFail,
    resetUserManagementStateSuccess,
    resetUserManagementErrorState,
} = userManagementSlide.actions;

export const getUserManagementList = (state) => state.userManagement?.listData;
export const getUserManagementListPage = (state) => state.userManagement?.page;
export const getUserManagementListPageCount = (state) => state.userManagement?.pageCount;
export const getUserManagementListTotal = (state) => state.userManagement?.totalRow;
export const getUserManagementSummary = (state) => state.userManagement?.summaryData;
export const getUserManagementAssignableRoles = (state) => state.userManagement?.assignableRoles;
export const getUserManagementDetail = (state) => state.userManagement?.detailData;
export const getUserManagementErrorDetailData = (state) => state.userManagement?.errorDetailData;
export const getAddUserManagementSuccess = (state) => state.userManagement?.success;
export const getAddUserManagementFail = (state) => state.userManagement?.fail;
export const getAddUserManagementResponseData = (state) => state.userManagement?.responseData;

export default userManagementSlide.reducer;
